<section class="operations-page">
    <div class="operations-toolbar" *ngIf="canFilterByOrganization">
        <label class="operations-form-field">
            <span>Organization</span>
            <select [(ngModel)]="selectedOrgId" (ngModelChange)="onOrganizationChange()" [disabled]="isReadOnly">
                <option value="">Select organization</option>
                <option *ngFor="let organization of organizations" [value]="organization.id">
                    {{ organization.name }}
                </option>
            </select>
        </label>
    </div>

    <div class="operations-page__card operations-page__card--info" *ngIf="isReadOnly && !loading && settingsState">
        <p class="vision-settings__info-title">Read-only access</p>
        <p class="vision-settings__info-copy">
            Vision configuration can only be changed by a KYRA platform administrator.
        </p>
    </div>

    <div class="operations-page__card" *ngIf="loading">
        <p class="operations-page__loading">Loading organization vision settings…</p>
    </div>

    <div class="operations-page__card operations-page__card--error" *ngIf="!loading && errorMessage">
        <p>{{ errorMessage }}</p>
    </div>

    <ng-container *ngIf="!loading && settingsState">
        <div class="vision-settings__grid">
            <section class="operations-page__card">
                <h2 class="operations-page__heading">Vision mode &amp; policies</h2>
                <p class="operations-page__section-lead">
                    Control how detections are monitored and how verification affects access.
                </p>

                <label class="operations-form-field">
                    <span>
                        Vision mode
                        <abbr class="operations-page__help"
                              title="Disabled is automatic when no organization-level Vision capabilities are enabled. Monitor Only is automatic when organization-level Vision capabilities become active. Enforced is a manual administrator decision. Vision Mode controls Vision Verification only — not Vehicle Detection or camera analytics.">ⓘ</abbr>
                    </span>
                    <select [(ngModel)]="form.vision_mode"
                            [disabled]="isReadOnly || !hasVisionAnalyticsEnabled"
                            (ngModelChange)="onVisionModeChange()">
                        <option *ngFor="let option of visionModeOptions" [ngValue]="option.value">
                            {{ option.label }}
                        </option>
                    </select>
                    <p class="operations-form-hint">{{ customerVisionModeLifecycleHint(form.vision_mode) }}</p>
                </label>

                <div class="vision-settings__derived" *ngIf="derivedVerificationProvider || showImplementationDetails">
                    <span class="vision-settings__derived-label">
                        Verification provider
                        <abbr class="operations-page__help"
                              title="Derived automatically from enabled verification capabilities on this organization’s Vision Policy.">ⓘ</abbr>
                    </span>
                    <p class="vision-settings__derived-value">
                        {{ showImplementationDetails
                        ? (derivedVerificationProvider || 'Not required')
                        : verificationProviderLabel(derivedVerificationProvider) }}
                    </p>
                    <p class="operations-form-hint" *ngIf="showImplementationDetails">
                        Auto-derived from organization-level verification capabilities on this Vision Policy.
                        Not inferred from vision mode or camera analytics.
                    </p>
                </div>

                <label class="operations-form-field">
                    <span>
                        Enforce threshold
                        <abbr class="operations-page__help"
                              title="Minimum confidence score required to pass enforced verification. Only applies when Vision mode is Enforced.">ⓘ</abbr>
                    </span>
                    <input type="number" min="0" max="1" step="0.01"
                           [(ngModel)]="form.enforce_threshold"
                           [disabled]="isReadOnly || !isEnforceThresholdEditable"
                           (ngModelChange)="onFormChange()">
                </label>

                <label class="operations-form-field">
                    <span>
                        Failure policy
                        <abbr class="operations-page__help"
                              title="Fail open allows access when verification is unavailable. Fail closed denies access.">ⓘ</abbr>
                    </span>
                    <select [(ngModel)]="form.vision_failure_policy"
                            [disabled]="isReadOnly"
                            (ngModelChange)="onFormChange()">
                        <option *ngFor="let option of failurePolicyOptions" [ngValue]="option.value">
                            {{ option.label }}
                        </option>
                    </select>
                </label>

                <label class="operations-form-field">
                    <span>
                        Vision timeout (seconds)
                        <abbr class="operations-page__help"
                              title="How long to wait for verification results before applying the failure policy.">ⓘ</abbr>
                    </span>
                    <input type="number" min="1" step="1"
                           [(ngModel)]="form.vision_timeout_seconds"
                           [disabled]="isReadOnly"
                           (ngModelChange)="onFormChange()">
                </label>

                <div class="vision-settings__capability-editor">
                    <h3 class="vision-settings__subheading">Organization Vision capabilities</h3>
                    <p class="operations-page__section-lead operations-page__section-lead--compact">
                        These capabilities define organization-level Vision Policy and verification behavior.
                        They do not enable analytics on individual cameras.
                    </p>

                    <p class="vision-settings__group-label">Vehicle Detection <span>(exactly one)</span></p>
                    <ul class="vision-settings__capabilities" *ngIf="vehicleDetectionDefinitions.length">
                        <li *ngFor="let definition of vehicleDetectionDefinitions">
                            <label class="vision-settings__checkbox"
                                   [class.vision-settings__checkbox--disabled]="isReadOnly"
                                   [class.vision-settings__checkbox--enabled]="isCapabilityEnabled(definition.analytics_key)">
                                <span class="vision-settings__check"
                                      *ngIf="isCapabilityEnabled(definition.analytics_key)"
                                      aria-hidden="true">✓</span>
                                <input type="radio"
                                       name="org-vehicle-detection"
                                       [value]="definition.analytics_key"
                                       [checked]="isCapabilityEnabled(definition.analytics_key)"
                                       [disabled]="isReadOnly"
                                       (change)="onVehicleDetectionChange(definition.analytics_key)">
                                <span>
                                    <strong>{{ showImplementationDetails ? definition.name : customerDefinitionName(definition) }}</strong>
                                    <small *ngIf="showImplementationDetails">
                                        {{ definition.analytics_key }} · {{ implementationProviderLabel(definition.provider) }}
                                    </small>
                                    <small *ngIf="!showImplementationDetails && customerDefinitionDescription(definition) as description">
                                        {{ description }}
                                    </small>
                                </span>
                            </label>
                        </li>
                    </ul>

                    <p class="vision-settings__group-label">Vision Analytics <span>(optional)</span></p>
                    <ul class="vision-settings__capabilities" *ngIf="visionAnalyticsDefinitions.length">
                        <li *ngFor="let definition of visionAnalyticsDefinitions">
                            <label class="vision-settings__checkbox"
                                   [class.vision-settings__checkbox--disabled]="isReadOnly"
                                   [class.vision-settings__checkbox--enabled]="isCapabilityEnabled(definition.analytics_key)">
                                <span class="vision-settings__check"
                                      *ngIf="isCapabilityEnabled(definition.analytics_key)"
                                      aria-hidden="true">✓</span>
                                <input type="checkbox"
                                       [checked]="isCapabilityEnabled(definition.analytics_key)"
                                       [disabled]="isReadOnly"
                                       (change)="onVisionAnalyticsChange(definition.analytics_key, $any($event.target).checked)">
                                <span>
                                    <strong>{{ showImplementationDetails ? definition.name : customerDefinitionName(definition) }}</strong>
                                    <small *ngIf="showImplementationDetails">
                                        {{ definition.analytics_key }} · {{ implementationProviderLabel(definition.provider) }}
                                    </small>
                                    <small *ngIf="!showImplementationDetails && customerDefinitionDescription(definition) as description">
                                        {{ description }}
                                    </small>
                                </span>
                            </label>
                        </li>
                    </ul>
                    <div class="operations-page__empty operations-page__empty--inline"
                         *ngIf="!vehicleDetectionDefinitions.length && !visionAnalyticsDefinitions.length">
                        No active analytics definitions are available.
                    </div>
                </div>

            </section>

            <section class="operations-page__card">
                <h2 class="operations-page__heading">Enabled analytics</h2>
                <p class="operations-page__section-lead">
                    Analytics currently configured across cameras in this organization.
                    This is a read-only camera-derived summary — camera analytics are configured independently for each camera.
                    This summary does not control Vision Mode or organization Vision capabilities.
                </p>

                <div class="operations-page__empty operations-page__empty--inline" *ngIf="!analyticsSummary.length">
                    No cameras currently have analytics enabled.
                </div>

                <ul class="vision-settings__capabilities" *ngIf="analyticsSummary.length">
                    <li *ngFor="let item of analyticsSummary">
                        <div class="vision-settings__checkbox vision-settings__checkbox--disabled vision-settings__checkbox--enabled vision-settings__summary-row"
                             role="listitem">
                            <span class="vision-settings__check" aria-hidden="true">✓</span>
                            <span>
                                <strong>{{ showImplementationDetails ? item.name : customerSummaryName(item) }}</strong>
                                <small *ngIf="showImplementationDetails">
                                    {{ item.analytics_key }} · {{ implementationProviderLabel(item.provider) }}
                                </small>
                            </span>
                        </div>
                    </li>
                </ul>
            </section>
        </div>

        <app-metadata-panel
                *ngIf="showImplementationDetails"
                title="Feature flags"
                [entries]="featureFlagEntries">
        </app-metadata-panel>

        <div class="operations-form-actions" *ngIf="canEdit">
            <p class="operations-unsaved" *ngIf="hasUnsavedChanges">You have unsaved changes.</p>
            <button type="button"
                    class="operations-btn"
                    [disabled]="saving || !hasUnsavedChanges"
                    (click)="resetForm()">
                Reset
            </button>
            <button type="button"
                    class="operations-btn operations-btn--primary"
                    [disabled]="!canSaveOrganization"
                    (click)="saveSettings()">
                {{ saving ? 'Saving…' : 'Save changes' }}
            </button>
        </div>
    </ng-container>
</section>
